// Blog article translations - Batch 4 (articles 16-20)
// This file contains translated content for 5 additional blog articles

export const blogArticleTranslationsBatch4 = {
  en: {
    // Article 16: extra-packages-ota-bookings (2021-04-28)
    'blog.article.extra-packages.title': 'New Possibilities to Charge Extra Services',
    'blog.article.extra-packages.excerpt': 'Bundle multiple extras into a single package for OTA bookings — ensuring accurate pricing while maintaining full transparency on your own website.',
    'blog.article.extra-packages.content': `
## Improved Extras Tool in i-Rent

We've enhanced the extras functionality in i-Rent with powerful new features designed to solve a common industry challenge.

## The Problem

OTA platforms offer limited options for calculating extras in online bookings. Typically, extras that OTAs cannot handle are simply added as text in the accommodation description. The result? Guests often overlook this information, leading to disputes and uncomfortable discussions at check-in.

## The Solution: Extra Packages

You can now bundle multiple extras into a single **extra package**.

Here's how it works:

1. **Create an extra package** combining several individual extras
2. **i-Rent sends this package as one item** to OTAs that support it
3. **The OTA includes it in the booking calculation** — no hidden surprises
4. **When the booking returns to i-Rent**, the extras are automatically split back into separate line items

## How It Works on Your Website

On your own website, extra packages behave differently:

- Individual extras are displayed separately for full transparency
- You can choose to **include certain items invisibly** in the rental price
- Or **show them as separate line items** in the price breakdown

This flexibility allows you to handle items like administration fees, bed linen, or mid-stay cleaning without raising questions from guests.

## Common Use Cases

- **Administration costs** — Include in the base price seamlessly
- **Bed linen and towels** — Bundle as a mandatory package
- **Cleaning during stay** — Add to longer bookings automatically
- **Welcome packages** — Combine multiple items into one extra

## Get Started

To discover the full potential of extra packages for your properties, contact our helpdesk for personalized assistance. We'll help you set up a configuration that works best for your business.
    `,

    // Article 17: flexible-pricing-ota-partners (2021-05-15)
    'blog.article.ota-pricing.title': 'Flexible Price Adjustments for OTAs and Agents',
    'blog.article.ota-pricing.excerpt': 'Take control of your revenue strategy with flexible price adjustments — set custom markups per partner, per property, and per period directly in i-Rent.',
    'blog.article.ota-pricing.content': `
## A New Tool for Smarter Pricing

We've added a powerful new feature to i-Rent that gives you complete control over how prices are adjusted for your rental partners, including OTAs and agents.

You'll find this tool under **Administrator → External Price Settings**.

## What Can You Do?

With this feature, you can adjust prices:

- **Per rental partner** — Apply different markups for Booking.com, Airbnb, agents, etc.
- **Per accommodation** — Set specific adjustments for individual properties or groups
- **Per time period** — Increase prices during peak seasons or reduce them in low season

## Step-by-Step Setup

### Step 1: Define Your Price Adjustment

Choose a name for the adjustment, set the percentage increase (or decrease), and select the date range.

### Step 2: Select Accommodations

Choose which properties this adjustment applies to — you can select all or pick specific ones.

### Step 3: Select Partners

Choose which rental partners should receive this price adjustment.

## Unlimited Flexibility

You can create as many different price adjustments as you need:

- Different markups for different partner groups
- Seasonal variations across your portfolio
- Property-specific strategies for high-demand accommodations

**Pro tip:** You can also set negative percentages to reduce prices — perfect for offering special rates to preferred partners or during slower periods.

## Start Optimizing Your Revenue

This feature puts you in control of your pricing strategy across all channels. Set it up once and let i-Rent handle the rest automatically.
    `,

    // Article 18: marriott-homes-villas-channel-manager (2021-05-20)
    'blog.article.marriott.title': 'New Channel: Homes & Villas by Marriott International',
    'blog.article.marriott.excerpt': 'Reach premium travelers worldwide through Marriott\'s curated holiday rental platform — now available via i-Rent\'s Channel Manager.',
    'blog.article.marriott.content': `
## Introducing Homes & Villas by Marriott International

[Homes & Villas](https://homes-and-villas.marriott.com/) is Marriott International's global holiday rental offering, launched in 2019. With over 90 years of hospitality expertise, Marriott has brought its legendary service standards to the holiday rental market.

## A Focus on Premium Properties

Unlike mass-market booking platforms, Homes & Villas focuses exclusively on **premium and luxury rental properties**. Marriott carefully selects professional property managers as partners, ensuring that every listed property meets the high standards guests expect from the Marriott name.

## Why This Matters for Your Business

### Access to Marriott's Global Audience
With Marriott's vast customer base of loyal travelers, listing on Homes & Villas gives your premium properties exposure to millions of potential guests who value quality and reliability.

### Professional Standards
Being part of the Marriott network signals to guests that your property has been vetted and meets international hospitality standards — building instant trust.

### Seamless Integration
Through i-Rent's Channel Manager, you can now connect your listings to Homes & Villas with full synchronization of availability, pricing, and bookings.

## How to Get Started

1. **Ensure your properties meet premium standards** — Marriott focuses on high-quality rentals
2. **Contact i-Rent support** to express your interest in the Homes & Villas channel
3. **Complete the partner application** with Marriott
4. **Start receiving bookings** synced directly to your i-Rent dashboard

## Elevate Your Portfolio

If you manage premium holiday rentals, Homes & Villas by Marriott International offers an excellent opportunity to reach discerning travelers who expect nothing but the best. Contact us to learn more about connecting your properties to this prestigious platform.
    `,

    // Article 19: bed-sizes-accommodation-description (2022-01-12)
    'blog.article.bed-sizes.title': 'Add Bed Sizes to Your Accommodation Description',
    'blog.article.bed-sizes.excerpt': 'Bed sizes matter to guests — now you can include them in your property descriptions automatically, reducing questions and helping travelers book with confidence.',
    'blog.article.bed-sizes.content': `
## Why Bed Sizes Matter

Bed sizes are often a deciding factor for guests when choosing their accommodation. From couples seeking a king-size bed to families needing specific arrangements for children, this information directly influences booking decisions.

By including bed dimensions in your property descriptions, you reduce guest inquiries, avoid misunderstandings, and create a more transparent booking experience.

## How to Add Bed Sizes in i-Rent

Follow these simple steps to include bed sizes in your accommodation descriptions:

1. **Open the accommodation info sheet** in i-Rent.net
2. Navigate to the **floor section** where bedrooms are described
3. Look for the option to **add or edit beds**
4. Select the appropriate bed types and sizes from the available options
5. **Save the info sheet**
6. Go to the **accommodation description** and regenerate it to include the updated bed information

## Important Note

When regenerating the accommodation description, any **manual text changes** you previously made will be overwritten.

If you've customized the description text, be sure to **copy and save those changes** before regenerating — you can add them back afterwards.

## Benefits of Including Bed Sizes

- **Fewer guest questions** — Provide the information travelers need upfront
- **Faster booking decisions** — Help guests confirm the property meets their needs
- **Reduced complaints** — Set clear expectations before arrival
- **Professional presentation** — Show attention to detail in your listings

## Start Updating Your Listings

Take a few minutes to add bed sizes to your property info sheets. This small improvement can make a big difference in guest satisfaction and booking conversions.
    `,

    // Article 20: price-adjustment-calculation-otas (2022-03-18)
    'blog.article.price-calculation.title': 'Improved Price Adjustment Calculation for OTAs and Agents',
    'blog.article.price-calculation.excerpt': 'We\'ve updated how price markups are calculated — ensuring your base rental amount is preserved accurately when applying percentage increases for channels.',
    'blog.article.price-calculation.content': `
## A Smarter Way to Calculate Price Markups

We've made an important change to how price increases are calculated when publishing to OTAs and agents. This update ensures your pricing remains accurate and predictable.

## The Problem with the Old Method

Previously, price markups were calculated by adding a percentage to the base price:

**Example:** Base rental price €900 + 10% (€90) = OTA price €990

The issue? When the OTA deducts their 10% commission from €990, you receive €891 — not your original €900 base price. This created a gap in your expected revenue.

## The New Calculation Method

The updated calculation works backwards from the selling price, ensuring that after the OTA deducts their percentage, you receive exactly your base rental amount.

In other words: if you set a 10% markup and the OTA takes 10%, your net result is now precisely €900 — as intended.

## Visual Updates in the Booking Page

To provide full transparency, we've updated the booking interface:

- Under the **Price tab**, the markup amount is now displayed in blue
- You can always see your base rental amount and the added increase separately
- A new column in the **General Booking Overview** shows these amounts at a glance
- An explanatory message appears in the green info field on the booking page

## Protect Your Margins

This update helps you maintain healthy margins when working with OTAs and agents. No more unexpected revenue gaps — just accurate, predictable pricing across all your channels.
    `,
  },

  es: {
    // Article 16: extra-packages-ota-bookings
    'blog.article.extra-packages.title': 'Nuevas Posibilidades para Cobrar Servicios Extra',
    'blog.article.extra-packages.excerpt': 'Agrupa múltiples extras en un solo paquete para reservas de OTAs — asegurando precios precisos mientras mantienes total transparencia en tu propio sitio web.',
    'blog.article.extra-packages.content': `
## Herramienta de Extras Mejorada en i-Rent

Hemos mejorado la funcionalidad de extras en i-Rent con nuevas características potentes diseñadas para resolver un desafío común de la industria.

## El Problema

Las plataformas OTA ofrecen opciones limitadas para calcular extras en reservas online. Normalmente, los extras que las OTAs no pueden manejar simplemente se agregan como texto en la descripción del alojamiento. ¿El resultado? Los huéspedes frecuentemente pasan por alto esta información, lo que lleva a disputas y discusiones incómodas en el check-in.

## La Solución: Paquetes de Extras

Ahora puedes agrupar múltiples extras en un solo **paquete de extras**.

Así es como funciona:

1. **Crea un paquete de extras** combinando varios extras individuales
2. **i-Rent envía este paquete como un solo artículo** a las OTAs que lo soportan
3. **La OTA lo incluye en el cálculo de la reserva** — sin sorpresas ocultas
4. **Cuando la reserva regresa a i-Rent**, los extras se dividen automáticamente en líneas separadas

## Cómo Funciona en Tu Sitio Web

En tu propio sitio web, los paquetes de extras se comportan diferente:

- Los extras individuales se muestran por separado para total transparencia
- Puedes elegir **incluir ciertos artículos invisiblemente** en el precio del alquiler
- O **mostrarlos como líneas separadas** en el desglose de precios

Esta flexibilidad te permite manejar artículos como gastos de administración, ropa de cama o limpieza durante la estancia sin generar preguntas de los huéspedes.

## Casos de Uso Comunes

- **Costos de administración** — Incluir en el precio base sin problemas
- **Ropa de cama y toallas** — Agrupar como paquete obligatorio
- **Limpieza durante la estancia** — Agregar automáticamente a reservas largas
- **Paquetes de bienvenida** — Combinar múltiples artículos en un extra

## Comienza

Para descubrir todo el potencial de los paquetes de extras para tus propiedades, contacta a nuestro servicio de ayuda para asistencia personalizada. Te ayudaremos a configurar una configuración que funcione mejor para tu negocio.
    `,

    // Article 17: flexible-pricing-ota-partners
    'blog.article.ota-pricing.title': 'Ajustes de Precios Flexibles para OTAs y Agentes',
    'blog.article.ota-pricing.excerpt': 'Toma el control de tu estrategia de ingresos con ajustes de precios flexibles — establece márgenes personalizados por partner, por propiedad y por período directamente en i-Rent.',
    'blog.article.ota-pricing.content': `
## Una Nueva Herramienta para Precios Más Inteligentes

Hemos añadido una nueva característica potente a i-Rent que te da control completo sobre cómo se ajustan los precios para tus socios de alquiler, incluyendo OTAs y agentes.

Encontrarás esta herramienta en **Administrador → Configuración de Precios Externos**.

## ¿Qué Puedes Hacer?

Con esta característica, puedes ajustar precios:

- **Por socio de alquiler** — Aplica diferentes márgenes para Booking.com, Airbnb, agentes, etc.
- **Por alojamiento** — Establece ajustes específicos para propiedades individuales o grupos
- **Por período de tiempo** — Aumenta precios durante temporada alta o redúcelos en temporada baja

## Configuración Paso a Paso

### Paso 1: Define Tu Ajuste de Precio

Elige un nombre para el ajuste, establece el porcentaje de aumento (o disminución) y selecciona el rango de fechas.

### Paso 2: Selecciona Alojamientos

Elige a qué propiedades aplica este ajuste — puedes seleccionar todas o elegir específicas.

### Paso 3: Selecciona Socios

Elige qué socios de alquiler deben recibir este ajuste de precio.

## Flexibilidad Ilimitada

Puedes crear tantos ajustes de precios diferentes como necesites:

- Diferentes márgenes para diferentes grupos de socios
- Variaciones estacionales en tu portfolio
- Estrategias específicas por propiedad para alojamientos de alta demanda

**Consejo profesional:** También puedes establecer porcentajes negativos para reducir precios — perfecto para ofrecer tarifas especiales a socios preferidos o durante períodos más lentos.

## Comienza a Optimizar Tus Ingresos

Esta característica te pone en control de tu estrategia de precios en todos los canales. Configúralo una vez y deja que i-Rent maneje el resto automáticamente.
    `,

    // Article 18: marriott-homes-villas-channel-manager
    'blog.article.marriott.title': 'Nuevo Canal: Homes & Villas de Marriott International',
    'blog.article.marriott.excerpt': 'Llega a viajeros premium en todo el mundo a través de la plataforma curada de alquileres vacacionales de Marriott — ahora disponible vía el Channel Manager de i-Rent.',
    'blog.article.marriott.content': `
## Presentamos Homes & Villas de Marriott International

[Homes & Villas](https://homes-and-villas.marriott.com/) es la oferta global de alquiler vacacional de Marriott International, lanzada en 2019. Con más de 90 años de experiencia en hospitalidad, Marriott ha traído sus legendarios estándares de servicio al mercado de alquiler vacacional.

## Un Enfoque en Propiedades Premium

A diferencia de las plataformas de reservas masivas, Homes & Villas se enfoca exclusivamente en **propiedades de alquiler premium y de lujo**. Marriott selecciona cuidadosamente a gestores de propiedades profesionales como socios, asegurando que cada propiedad listada cumple con los altos estándares que los huéspedes esperan del nombre Marriott.

## Por Qué Esto Importa para Tu Negocio

### Acceso a la Audiencia Global de Marriott
Con la vasta base de clientes de Marriott de viajeros leales, listar en Homes & Villas da a tus propiedades premium exposición a millones de huéspedes potenciales que valoran la calidad y fiabilidad.

### Estándares Profesionales
Ser parte de la red Marriott señala a los huéspedes que tu propiedad ha sido verificada y cumple con estándares de hospitalidad internacionales — construyendo confianza instantánea.

### Integración Sin Problemas
A través del Channel Manager de i-Rent, ahora puedes conectar tus listings a Homes & Villas con sincronización completa de disponibilidad, precios y reservas.

## Cómo Empezar

1. **Asegúrate de que tus propiedades cumplan estándares premium** — Marriott se enfoca en alquileres de alta calidad
2. **Contacta al soporte de i-Rent** para expresar tu interés en el canal Homes & Villas
3. **Completa la solicitud de socio** con Marriott
4. **Comienza a recibir reservas** sincronizadas directamente a tu panel de i-Rent

## Eleva Tu Portfolio

Si gestionas alquileres vacacionales premium, Homes & Villas de Marriott International ofrece una excelente oportunidad para llegar a viajeros exigentes que esperan nada menos que lo mejor. Contáctanos para aprender más sobre conectar tus propiedades a esta prestigiosa plataforma.
    `,

    // Article 19: bed-sizes-accommodation-description
    'blog.article.bed-sizes.title': 'Añade Tamaños de Cama a la Descripción de Tu Alojamiento',
    'blog.article.bed-sizes.excerpt': 'Los tamaños de cama importan a los huéspedes — ahora puedes incluirlos en las descripciones de tus propiedades automáticamente, reduciendo preguntas y ayudando a los viajeros a reservar con confianza.',
    'blog.article.bed-sizes.content': `
## Por Qué Importan los Tamaños de Cama

Los tamaños de cama son frecuentemente un factor decisivo para los huéspedes al elegir su alojamiento. Desde parejas buscando una cama king-size hasta familias necesitando arreglos específicos para niños, esta información influye directamente en las decisiones de reserva.

Al incluir las dimensiones de las camas en las descripciones de tus propiedades, reduces las consultas de huéspedes, evitas malentendidos y creas una experiencia de reserva más transparente.

## Cómo Añadir Tamaños de Cama en i-Rent

Sigue estos simples pasos para incluir tamaños de cama en las descripciones de tu alojamiento:

1. **Abre la ficha de información del alojamiento** en i-Rent.net
2. Navega a la **sección de planta** donde se describen los dormitorios
3. Busca la opción de **añadir o editar camas**
4. Selecciona los tipos y tamaños de cama apropiados de las opciones disponibles
5. **Guarda la ficha de información**
6. Ve a la **descripción del alojamiento** y regenerala para incluir la información actualizada de camas

## Nota Importante

Al regenerar la descripción del alojamiento, cualquier **cambio de texto manual** que hayas hecho previamente será sobrescrito.

Si has personalizado el texto de la descripción, asegúrate de **copiar y guardar esos cambios** antes de regenerar — puedes añadirlos después.

## Beneficios de Incluir Tamaños de Cama

- **Menos preguntas de huéspedes** — Proporciona la información que los viajeros necesitan por adelantado
- **Decisiones de reserva más rápidas** — Ayuda a los huéspedes a confirmar que la propiedad cumple sus necesidades
- **Menos quejas** — Establece expectativas claras antes de la llegada
- **Presentación profesional** — Muestra atención al detalle en tus listados

## Comienza a Actualizar Tus Listados

Toma unos minutos para añadir tamaños de cama a las fichas de información de tus propiedades. Esta pequeña mejora puede hacer una gran diferencia en la satisfacción del huésped y las conversiones de reservas.
    `,

    // Article 20: price-adjustment-calculation-otas
    'blog.article.price-calculation.title': 'Cálculo Mejorado de Ajuste de Precios para OTAs y Agentes',
    'blog.article.price-calculation.excerpt': 'Hemos actualizado cómo se calculan los márgenes de precios — asegurando que tu cantidad base de alquiler se preserve con precisión al aplicar aumentos porcentuales para canales.',
    'blog.article.price-calculation.content': `
## Una Forma Más Inteligente de Calcular Márgenes de Precio

Hemos hecho un cambio importante en cómo se calculan los aumentos de precio al publicar a OTAs y agentes. Esta actualización asegura que tus precios permanezcan precisos y predecibles.

## El Problema con el Método Antiguo

Anteriormente, los márgenes de precio se calculaban añadiendo un porcentaje al precio base:

**Ejemplo:** Precio base de alquiler €900 + 10% (€90) = Precio OTA €990

¿El problema? Cuando la OTA deduce su comisión del 10% de €990, recibes €891 — no tus €900 originales de precio base. Esto creaba una brecha en tus ingresos esperados.

## El Nuevo Método de Cálculo

El cálculo actualizado trabaja hacia atrás desde el precio de venta, asegurando que después de que la OTA deduzca su porcentaje, recibas exactamente tu cantidad base de alquiler.

En otras palabras: si estableces un margen del 10% y la OTA toma el 10%, tu resultado neto es ahora precisamente €900 — como se pretendía.

## Actualizaciones Visuales en la Página de Reserva

Para proporcionar total transparencia, hemos actualizado la interfaz de reservas:

- Bajo la **pestaña Precio**, el monto del margen ahora se muestra en azul
- Siempre puedes ver tu cantidad base de alquiler y el aumento añadido por separado
- Una nueva columna en el **Resumen General de Reservas** muestra estas cantidades de un vistazo
- Un mensaje explicativo aparece en el campo de información verde en la página de reserva

## Protege Tus Márgenes

Esta actualización te ayuda a mantener márgenes saludables al trabajar con OTAs y agentes. Sin más brechas de ingresos inesperadas — solo precios precisos y predecibles en todos tus canales.
    `,
  },

  de: {
    // Article 16: extra-packages-ota-bookings
    'blog.article.extra-packages.title': 'Neue Möglichkeiten zur Berechnung von Zusatzleistungen',
    'blog.article.extra-packages.excerpt': 'Bündeln Sie mehrere Extras zu einem Paket für OTA-Buchungen — gewährleisten Sie genaue Preisgestaltung bei voller Transparenz auf Ihrer eigenen Website.',
    'blog.article.extra-packages.content': `
## Verbessertes Extras-Tool in i-Rent

Wir haben die Extras-Funktionalität in i-Rent mit leistungsstarken neuen Funktionen erweitert, die eine häufige Branchenherausforderung lösen.

## Das Problem

OTA-Plattformen bieten begrenzte Optionen zur Berechnung von Extras bei Online-Buchungen. Typischerweise werden Extras, die OTAs nicht verarbeiten können, einfach als Text in der Unterkunftsbeschreibung hinzugefügt. Das Ergebnis? Gäste übersehen diese Informationen häufig, was zu Streitigkeiten und unangenehmen Diskussionen beim Check-in führt.

## Die Lösung: Extra-Pakete

Sie können jetzt mehrere Extras in einem einzigen **Extra-Paket** bündeln.

So funktioniert es:

1. **Erstellen Sie ein Extra-Paket**, das mehrere einzelne Extras kombiniert
2. **i-Rent sendet dieses Paket als einen Artikel** an OTAs, die es unterstützen
3. **Die OTA bezieht es in die Buchungsberechnung ein** — keine versteckten Überraschungen
4. **Wenn die Buchung zu i-Rent zurückkehrt**, werden die Extras automatisch wieder in separate Positionen aufgeteilt

## Wie Es Auf Ihrer Website Funktioniert

Auf Ihrer eigenen Website verhalten sich Extra-Pakete anders:

- Einzelne Extras werden separat für volle Transparenz angezeigt
- Sie können wählen, **bestimmte Artikel unsichtbar** in den Mietpreis einzubeziehen
- Oder sie als **separate Positionen** in der Preisaufschlüsselung anzuzeigen

Diese Flexibilität ermöglicht es Ihnen, Artikel wie Verwaltungsgebühren, Bettwäsche oder Zwischenreinigung zu handhaben, ohne Fragen von Gästen aufzuwerfen.

## Häufige Anwendungsfälle

- **Verwaltungskosten** — Nahtlos in den Grundpreis einbeziehen
- **Bettwäsche und Handtücher** — Als Pflichtpaket bündeln
- **Reinigung während des Aufenthalts** — Automatisch zu längeren Buchungen hinzufügen
- **Willkommenspakete** — Mehrere Artikel zu einem Extra kombinieren

## Loslegen

Um das volle Potenzial von Extra-Paketen für Ihre Unterkünfte zu entdecken, kontaktieren Sie unseren Helpdesk für persönliche Unterstützung. Wir helfen Ihnen bei der Einrichtung einer Konfiguration, die am besten für Ihr Unternehmen funktioniert.
    `,

    // Article 17: flexible-pricing-ota-partners
    'blog.article.ota-pricing.title': 'Flexible Preisanpassungen für OTAs und Agenten',
    'blog.article.ota-pricing.excerpt': 'Übernehmen Sie die Kontrolle über Ihre Umsatzstrategie mit flexiblen Preisanpassungen — setzen Sie individuelle Aufschläge pro Partner, pro Unterkunft und pro Zeitraum direkt in i-Rent.',
    'blog.article.ota-pricing.content': `
## Ein Neues Tool für Intelligentere Preisgestaltung

Wir haben eine leistungsstarke neue Funktion zu i-Rent hinzugefügt, die Ihnen vollständige Kontrolle darüber gibt, wie Preise für Ihre Vermietungspartner, einschließlich OTAs und Agenten, angepasst werden.

Sie finden dieses Tool unter **Administrator → Externe Preiseinstellungen**.

## Was Können Sie Tun?

Mit dieser Funktion können Sie Preise anpassen:

- **Pro Vermietungspartner** — Wenden Sie verschiedene Aufschläge für Booking.com, Airbnb, Agenten usw. an
- **Pro Unterkunft** — Setzen Sie spezifische Anpassungen für einzelne Unterkünfte oder Gruppen
- **Pro Zeitraum** — Erhöhen Sie Preise während der Hochsaison oder reduzieren Sie sie in der Nebensaison

## Schritt-für-Schritt Einrichtung

### Schritt 1: Definieren Sie Ihre Preisanpassung

Wählen Sie einen Namen für die Anpassung, setzen Sie den Prozentsatz der Erhöhung (oder Verringerung) und wählen Sie den Datumsbereich.

### Schritt 2: Wählen Sie Unterkünfte

Wählen Sie, für welche Unterkünfte diese Anpassung gilt — Sie können alle auswählen oder bestimmte auswählen.

### Schritt 3: Wählen Sie Partner

Wählen Sie, welche Vermietungspartner diese Preisanpassung erhalten sollen.

## Unbegrenzte Flexibilität

Sie können so viele verschiedene Preisanpassungen erstellen, wie Sie benötigen:

- Verschiedene Aufschläge für verschiedene Partnergruppen
- Saisonale Variationen in Ihrem Portfolio
- Unterkunftsspezifische Strategien für stark nachgefragte Unterkünfte

**Profi-Tipp:** Sie können auch negative Prozentsätze setzen, um Preise zu reduzieren — perfekt für Sondertarife für bevorzugte Partner oder während ruhigerer Zeiten.

## Beginnen Sie Ihre Umsatzoptimierung

Diese Funktion gibt Ihnen die Kontrolle über Ihre Preisstrategie über alle Kanäle hinweg. Richten Sie es einmal ein und lassen Sie i-Rent den Rest automatisch erledigen.
    `,

    // Article 18: marriott-homes-villas-channel-manager
    'blog.article.marriott.title': 'Neuer Kanal: Homes & Villas von Marriott International',
    'blog.article.marriott.excerpt': 'Erreichen Sie Premium-Reisende weltweit über Marriotts kuratierte Ferienvermietungsplattform — jetzt verfügbar über i-Rents Channel Manager.',
    'blog.article.marriott.content': `
## Vorstellung von Homes & Villas von Marriott International

[Homes & Villas](https://homes-and-villas.marriott.com/) ist das globale Ferienvermietungsangebot von Marriott International, das 2019 gestartet wurde. Mit über 90 Jahren Gastfreundschaftserfahrung hat Marriott seine legendären Servicestandards in den Ferienvermietungsmarkt gebracht.

## Fokus auf Premium-Unterkünfte

Im Gegensatz zu Massenmarkt-Buchungsplattformen konzentriert sich Homes & Villas ausschließlich auf **Premium- und Luxusmietunterkünfte**. Marriott wählt sorgfältig professionelle Immobilienverwalter als Partner aus und stellt sicher, dass jede gelistete Unterkunft die hohen Standards erfüllt, die Gäste vom Namen Marriott erwarten.

## Warum Dies Für Ihr Unternehmen Wichtig Ist

### Zugang zu Marriotts Globalem Publikum
Mit Marriotts riesigem Kundenstamm treuer Reisender gibt Ihnen eine Listung auf Homes & Villas Ihren Premium-Unterkünften Zugang zu Millionen potenzieller Gäste, die Qualität und Zuverlässigkeit schätzen.

### Professionelle Standards
Teil des Marriott-Netzwerks zu sein, signalisiert Gästen, dass Ihre Unterkunft überprüft wurde und internationale Gastfreundschaftsstandards erfüllt — sofortiges Vertrauen aufbauend.

### Nahtlose Integration
Über i-Rents Channel Manager können Sie jetzt Ihre Inserate mit Homes & Villas verbinden, mit voller Synchronisierung von Verfügbarkeit, Preisen und Buchungen.

## Wie Sie Beginnen

1. **Stellen Sie sicher, dass Ihre Unterkünfte Premium-Standards erfüllen** — Marriott konzentriert sich auf hochwertige Vermietungen
2. **Kontaktieren Sie den i-Rent Support**, um Ihr Interesse am Homes & Villas-Kanal zu bekunden
3. **Vervollständigen Sie die Partnerbewerbung** bei Marriott
4. **Beginnen Sie Buchungen zu empfangen**, die direkt mit Ihrem i-Rent Dashboard synchronisiert werden

## Heben Sie Ihr Portfolio

Wenn Sie Premium-Ferienvermietungen verwalten, bietet Homes & Villas von Marriott International eine ausgezeichnete Gelegenheit, anspruchsvolle Reisende zu erreichen, die nichts weniger als das Beste erwarten. Kontaktieren Sie uns, um mehr über die Verbindung Ihrer Unterkünfte mit dieser prestigeträchtigen Plattform zu erfahren.
    `,

    // Article 19: bed-sizes-accommodation-description
    'blog.article.bed-sizes.title': 'Fügen Sie Bettgrößen zu Ihrer Unterkunftsbeschreibung Hinzu',
    'blog.article.bed-sizes.excerpt': 'Bettgrößen sind für Gäste wichtig — jetzt können Sie sie automatisch in Ihre Unterkunftsbeschreibungen aufnehmen, Fragen reduzieren und Reisenden helfen, mit Vertrauen zu buchen.',
    'blog.article.bed-sizes.content': `
## Warum Bettgrößen Wichtig Sind

Bettgrößen sind oft ein entscheidender Faktor für Gäste bei der Wahl ihrer Unterkunft. Von Paaren, die ein Kingsize-Bett suchen, bis zu Familien, die spezifische Arrangements für Kinder benötigen, beeinflusst diese Information direkt Buchungsentscheidungen.

Indem Sie Bettmaße in Ihre Unterkunftsbeschreibungen aufnehmen, reduzieren Sie Gästeanfragen, vermeiden Missverständnisse und schaffen ein transparenteres Buchungserlebnis.

## Wie Sie Bettgrößen in i-Rent Hinzufügen

Befolgen Sie diese einfachen Schritte, um Bettgrößen in Ihre Unterkunftsbeschreibungen aufzunehmen:

1. **Öffnen Sie das Unterkunfts-Infosheet** in i-Rent.net
2. Navigieren Sie zum **Etagenabschnitt**, in dem Schlafzimmer beschrieben werden
3. Suchen Sie nach der Option zum **Hinzufügen oder Bearbeiten von Betten**
4. Wählen Sie die entsprechenden Betttypen und -größen aus den verfügbaren Optionen
5. **Speichern Sie das Infosheet**
6. Gehen Sie zur **Unterkunftsbeschreibung** und regenerieren Sie sie, um die aktualisierten Bettinformationen aufzunehmen

## Wichtiger Hinweis

Beim Regenerieren der Unterkunftsbeschreibung werden alle **manuellen Textänderungen**, die Sie zuvor vorgenommen haben, überschrieben.

Wenn Sie den Beschreibungstext angepasst haben, stellen Sie sicher, dass Sie **diese Änderungen kopieren und speichern**, bevor Sie regenerieren — Sie können sie danach wieder hinzufügen.

## Vorteile der Aufnahme von Bettgrößen

- **Weniger Gästefragen** — Stellen Sie die Informationen, die Reisende benötigen, im Voraus bereit
- **Schnellere Buchungsentscheidungen** — Helfen Sie Gästen zu bestätigen, dass die Unterkunft ihren Bedürfnissen entspricht
- **Weniger Beschwerden** — Setzen Sie klare Erwartungen vor der Ankunft
- **Professionelle Präsentation** — Zeigen Sie Liebe zum Detail in Ihren Inseraten

## Beginnen Sie Mit Der Aktualisierung Ihrer Inserate

Nehmen Sie sich ein paar Minuten Zeit, um Bettgrößen zu Ihren Unterkunfts-Infosheets hinzuzufügen. Diese kleine Verbesserung kann einen großen Unterschied bei der Gästezufriedenheit und Buchungskonversionen machen.
    `,

    // Article 20: price-adjustment-calculation-otas
    'blog.article.price-calculation.title': 'Verbesserte Preisanpassungsberechnung für OTAs und Agenten',
    'blog.article.price-calculation.excerpt': 'Wir haben aktualisiert, wie Preisaufschläge berechnet werden — um sicherzustellen, dass Ihr Basis-Mietbetrag bei der Anwendung von prozentualen Erhöhungen für Kanäle genau erhalten bleibt.',
    'blog.article.price-calculation.content': `
## Ein Intelligenterer Weg zur Berechnung von Preisaufschlägen

Wir haben eine wichtige Änderung daran vorgenommen, wie Preiserhöhungen bei der Veröffentlichung an OTAs und Agenten berechnet werden. Dieses Update stellt sicher, dass Ihre Preisgestaltung genau und vorhersagbar bleibt.

## Das Problem mit der Alten Methode

Früher wurden Preisaufschläge berechnet, indem ein Prozentsatz zum Grundpreis hinzugefügt wurde:

**Beispiel:** Basis-Mietpreis 900€ + 10% (90€) = OTA-Preis 990€

Das Problem? Wenn die OTA ihre 10% Provision von 990€ abzieht, erhalten Sie 891€ — nicht Ihre ursprünglichen 900€ Basispreis. Dies schuf eine Lücke in Ihren erwarteten Einnahmen.

## Die Neue Berechnungsmethode

Die aktualisierte Berechnung arbeitet rückwärts vom Verkaufspreis und stellt sicher, dass Sie nach Abzug des OTA-Prozentsatzes genau Ihren Basis-Mietbetrag erhalten.

Mit anderen Worten: Wenn Sie einen Aufschlag von 10% festlegen und die OTA 10% nimmt, ist Ihr Nettoergebnis jetzt genau 900€ — wie beabsichtigt.

## Visuelle Updates auf der Buchungsseite

Um volle Transparenz zu bieten, haben wir die Buchungsoberfläche aktualisiert:

- Unter dem **Preis-Tab** wird der Aufschlagsbetrag jetzt in blau angezeigt
- Sie können immer Ihren Basis-Mietbetrag und die hinzugefügte Erhöhung separat sehen
- Eine neue Spalte in der **Allgemeinen Buchungsübersicht** zeigt diese Beträge auf einen Blick
- Eine erklärende Nachricht erscheint im grünen Infofeld auf der Buchungsseite

## Schützen Sie Ihre Margen

Dieses Update hilft Ihnen, gesunde Margen bei der Arbeit mit OTAs und Agenten zu erhalten. Keine unerwarteten Einnahmelücken mehr — nur genaue, vorhersagbare Preisgestaltung über alle Ihre Kanäle.
    `,
  },

  fr: {
    // Article 16: extra-packages-ota-bookings
    'blog.article.extra-packages.title': 'Nouvelles Possibilités pour Facturer les Services Supplémentaires',
    'blog.article.extra-packages.excerpt': 'Regroupez plusieurs extras dans un seul forfait pour les réservations OTA — assurant une tarification précise tout en maintenant une transparence totale sur votre propre site web.',
    'blog.article.extra-packages.content': `
## Outil d'Extras Amélioré dans i-Rent

Nous avons amélioré la fonctionnalité des extras dans i-Rent avec de nouvelles fonctionnalités puissantes conçues pour résoudre un défi courant de l'industrie.

## Le Problème

Les plateformes OTA offrent des options limitées pour calculer les extras dans les réservations en ligne. Typiquement, les extras que les OTA ne peuvent pas gérer sont simplement ajoutés comme texte dans la description du logement. Le résultat ? Les clients négligent souvent ces informations, ce qui conduit à des litiges et des discussions inconfortables lors de l'enregistrement.

## La Solution : Forfaits d'Extras

Vous pouvez maintenant regrouper plusieurs extras dans un seul **forfait d'extras**.

Voici comment ça fonctionne :

1. **Créez un forfait d'extras** combinant plusieurs extras individuels
2. **i-Rent envoie ce forfait comme un seul article** aux OTA qui le supportent
3. **L'OTA l'inclut dans le calcul de la réservation** — pas de surprises cachées
4. **Quand la réservation revient à i-Rent**, les extras sont automatiquement séparés en lignes distinctes

## Comment Ça Fonctionne Sur Votre Site Web

Sur votre propre site web, les forfaits d'extras se comportent différemment :

- Les extras individuels sont affichés séparément pour une transparence totale
- Vous pouvez choisir d'**inclure certains articles invisiblement** dans le prix de location
- Ou de les **afficher comme lignes séparées** dans le détail du prix

Cette flexibilité vous permet de gérer des articles comme les frais d'administration, le linge de lit ou le ménage en cours de séjour sans soulever de questions des clients.

## Cas d'Usage Courants

- **Frais d'administration** — Inclure dans le prix de base sans accroc
- **Linge de lit et serviettes** — Regrouper comme forfait obligatoire
- **Ménage pendant le séjour** — Ajouter automatiquement aux réservations longues
- **Forfaits de bienvenue** — Combiner plusieurs articles en un extra

## Commencez

Pour découvrir le plein potentiel des forfaits d'extras pour vos propriétés, contactez notre service d'assistance pour une aide personnalisée. Nous vous aiderons à mettre en place une configuration qui fonctionne le mieux pour votre entreprise.
    `,

    // Article 17: flexible-pricing-ota-partners
    'blog.article.ota-pricing.title': 'Ajustements de Prix Flexibles pour les OTA et Agents',
    'blog.article.ota-pricing.excerpt': 'Prenez le contrôle de votre stratégie de revenus avec des ajustements de prix flexibles — définissez des majorations personnalisées par partenaire, par propriété et par période directement dans i-Rent.',
    'blog.article.ota-pricing.content': `
## Un Nouvel Outil pour une Tarification Plus Intelligente

Nous avons ajouté une nouvelle fonctionnalité puissante à i-Rent qui vous donne un contrôle complet sur la façon dont les prix sont ajustés pour vos partenaires de location, y compris les OTA et agents.

Vous trouverez cet outil sous **Administrateur → Paramètres de Prix Externes**.

## Que Pouvez-Vous Faire ?

Avec cette fonctionnalité, vous pouvez ajuster les prix :

- **Par partenaire de location** — Appliquez différentes majorations pour Booking.com, Airbnb, agents, etc.
- **Par logement** — Définissez des ajustements spécifiques pour des propriétés individuelles ou des groupes
- **Par période** — Augmentez les prix pendant la haute saison ou réduisez-les en basse saison

## Configuration Étape par Étape

### Étape 1 : Définissez Votre Ajustement de Prix

Choisissez un nom pour l'ajustement, définissez le pourcentage d'augmentation (ou de diminution), et sélectionnez la plage de dates.

### Étape 2 : Sélectionnez les Logements

Choisissez à quelles propriétés cet ajustement s'applique — vous pouvez toutes les sélectionner ou en choisir certaines.

### Étape 3 : Sélectionnez les Partenaires

Choisissez quels partenaires de location devraient recevoir cet ajustement de prix.

## Flexibilité Illimitée

Vous pouvez créer autant d'ajustements de prix différents que nécessaire :

- Différentes majorations pour différents groupes de partenaires
- Variations saisonnières dans votre portfolio
- Stratégies spécifiques par propriété pour les logements à forte demande

**Conseil pro :** Vous pouvez aussi définir des pourcentages négatifs pour réduire les prix — parfait pour offrir des tarifs spéciaux à des partenaires privilégiés ou pendant les périodes plus calmes.

## Commencez à Optimiser Vos Revenus

Cette fonctionnalité vous met aux commandes de votre stratégie de tarification sur tous les canaux. Configurez-la une fois et laissez i-Rent gérer le reste automatiquement.
    `,

    // Article 18: marriott-homes-villas-channel-manager
    'blog.article.marriott.title': 'Nouveau Canal : Homes & Villas par Marriott International',
    'blog.article.marriott.excerpt': 'Atteignez les voyageurs premium du monde entier via la plateforme de location de vacances sélectionnée de Marriott — maintenant disponible via le Channel Manager d\'i-Rent.',
    'blog.article.marriott.content': `
## Présentation de Homes & Villas par Marriott International

[Homes & Villas](https://homes-and-villas.marriott.com/) est l'offre mondiale de location de vacances de Marriott International, lancée en 2019. Avec plus de 90 ans d'expertise en hospitalité, Marriott a apporté ses standards de service légendaires au marché de la location de vacances.

## Un Focus sur les Propriétés Premium

Contrairement aux plateformes de réservation de masse, Homes & Villas se concentre exclusivement sur les **propriétés de location premium et de luxe**. Marriott sélectionne soigneusement des gestionnaires de propriétés professionnels comme partenaires, s'assurant que chaque propriété listée répond aux standards élevés que les clients attendent du nom Marriott.

## Pourquoi C'est Important pour Votre Entreprise

### Accès à l'Audience Mondiale de Marriott
Avec la vaste base de clients de Marriott composée de voyageurs fidèles, lister sur Homes & Villas donne à vos propriétés premium une exposition à des millions de clients potentiels qui valorisent la qualité et la fiabilité.

### Standards Professionnels
Faire partie du réseau Marriott signale aux clients que votre propriété a été vérifiée et répond aux standards d'hospitalité internationaux — construisant une confiance instantanée.

### Intégration Transparente
Via le Channel Manager d'i-Rent, vous pouvez maintenant connecter vos annonces à Homes & Villas avec une synchronisation complète de la disponibilité, des prix et des réservations.

## Comment Commencer

1. **Assurez-vous que vos propriétés répondent aux standards premium** — Marriott se concentre sur les locations de haute qualité
2. **Contactez le support i-Rent** pour exprimer votre intérêt pour le canal Homes & Villas
3. **Complétez la demande de partenariat** avec Marriott
4. **Commencez à recevoir des réservations** synchronisées directement avec votre tableau de bord i-Rent

## Élevez Votre Portfolio

Si vous gérez des locations de vacances premium, Homes & Villas par Marriott International offre une excellente opportunité d'atteindre des voyageurs exigeants qui n'attendent rien de moins que le meilleur. Contactez-nous pour en savoir plus sur la connexion de vos propriétés à cette prestigieuse plateforme.
    `,

    // Article 19: bed-sizes-accommodation-description
    'blog.article.bed-sizes.title': 'Ajoutez les Tailles de Lit à Votre Description de Logement',
    'blog.article.bed-sizes.excerpt': 'Les tailles de lit sont importantes pour les clients — vous pouvez maintenant les inclure automatiquement dans vos descriptions de propriété, réduisant les questions et aidant les voyageurs à réserver en confiance.',
    'blog.article.bed-sizes.content': `
## Pourquoi les Tailles de Lit Sont Importantes

Les tailles de lit sont souvent un facteur décisif pour les clients lors du choix de leur logement. Des couples recherchant un lit king-size aux familles ayant besoin d'arrangements spécifiques pour les enfants, cette information influence directement les décisions de réservation.

En incluant les dimensions des lits dans vos descriptions de propriété, vous réduisez les demandes des clients, évitez les malentendus et créez une expérience de réservation plus transparente.

## Comment Ajouter les Tailles de Lit dans i-Rent

Suivez ces étapes simples pour inclure les tailles de lit dans vos descriptions de logement :

1. **Ouvrez la fiche d'information du logement** dans i-Rent.net
2. Naviguez vers la **section étage** où les chambres sont décrites
3. Recherchez l'option pour **ajouter ou modifier les lits**
4. Sélectionnez les types et tailles de lit appropriés parmi les options disponibles
5. **Enregistrez la fiche d'information**
6. Allez à la **description du logement** et régénérez-la pour inclure les informations de lit mises à jour

## Note Importante

Lors de la régénération de la description du logement, toutes les **modifications de texte manuelles** que vous avez précédemment effectuées seront écrasées.

Si vous avez personnalisé le texte de description, assurez-vous de **copier et sauvegarder ces modifications** avant de régénérer — vous pouvez les rajouter ensuite.

## Avantages d'Inclure les Tailles de Lit

- **Moins de questions des clients** — Fournissez les informations dont les voyageurs ont besoin à l'avance
- **Décisions de réservation plus rapides** — Aidez les clients à confirmer que la propriété répond à leurs besoins
- **Moins de plaintes** — Établissez des attentes claires avant l'arrivée
- **Présentation professionnelle** — Montrez l'attention aux détails dans vos annonces

## Commencez à Mettre à Jour Vos Annonces

Prenez quelques minutes pour ajouter les tailles de lit à vos fiches d'information de propriété. Cette petite amélioration peut faire une grande différence dans la satisfaction des clients et les conversions de réservation.
    `,

    // Article 20: price-adjustment-calculation-otas
    'blog.article.price-calculation.title': 'Calcul d\'Ajustement de Prix Amélioré pour les OTA et Agents',
    'blog.article.price-calculation.excerpt': 'Nous avons mis à jour la façon dont les majorations de prix sont calculées — assurant que votre montant de location de base est préservé avec précision lors de l\'application d\'augmentations en pourcentage pour les canaux.',
    'blog.article.price-calculation.content': `
## Une Façon Plus Intelligente de Calculer les Majorations de Prix

Nous avons apporté un changement important à la façon dont les augmentations de prix sont calculées lors de la publication vers les OTA et agents. Cette mise à jour assure que votre tarification reste précise et prévisible.

## Le Problème avec l'Ancienne Méthode

Auparavant, les majorations de prix étaient calculées en ajoutant un pourcentage au prix de base :

**Exemple :** Prix de location de base 900€ + 10% (90€) = Prix OTA 990€

Le problème ? Quand l'OTA déduit sa commission de 10% de 990€, vous recevez 891€ — pas vos 900€ de prix de base original. Cela créait un écart dans vos revenus attendus.

## La Nouvelle Méthode de Calcul

Le calcul mis à jour fonctionne à rebours depuis le prix de vente, assurant qu'après que l'OTA déduit son pourcentage, vous recevez exactement votre montant de location de base.

En d'autres termes : si vous définissez une majoration de 10% et que l'OTA prend 10%, votre résultat net est maintenant précisément 900€ — comme prévu.

## Mises à Jour Visuelles sur la Page de Réservation

Pour fournir une transparence totale, nous avons mis à jour l'interface de réservation :

- Sous l'**onglet Prix**, le montant de la majoration est maintenant affiché en bleu
- Vous pouvez toujours voir votre montant de location de base et l'augmentation ajoutée séparément
- Une nouvelle colonne dans l'**Aperçu Général des Réservations** montre ces montants en un coup d'œil
- Un message explicatif apparaît dans le champ d'info vert sur la page de réservation

## Protégez Vos Marges

Cette mise à jour vous aide à maintenir des marges saines lorsque vous travaillez avec les OTA et agents. Plus d'écarts de revenus inattendus — juste une tarification précise et prévisible sur tous vos canaux.
    `,
  },

  nl: {
    // Article 16: extra-packages-ota-bookings
    'blog.article.extra-packages.title': 'Nieuwe Mogelijkheden om Extra Diensten in Rekening te Brengen',
    'blog.article.extra-packages.excerpt': 'Bundel meerdere extra\'s in één pakket voor OTA-boekingen — met nauwkeurige prijzen en volledige transparantie op uw eigen website.',
    'blog.article.extra-packages.content': `
## Verbeterde Extra's Tool in i-Rent

We hebben de extra's functionaliteit in i-Rent verbeterd met krachtige nieuwe functies ontworpen om een veelvoorkomende uitdaging in de sector op te lossen.

## Het Probleem

OTA-platforms bieden beperkte opties voor het berekenen van extra's bij online boekingen. Meestal worden extra's die OTA's niet kunnen verwerken simpelweg toegevoegd als tekst in de accommodatiebeschrijving. Het resultaat? Gasten negeren deze informatie vaak, wat leidt tot geschillen en ongemakkelijke discussies bij het inchecken.

## De Oplossing: Extra Pakketten

U kunt nu meerdere extra's bundelen in één **extra pakket**.

Zo werkt het:

1. **Maak een extra pakket** dat verschillende individuele extra's combineert
2. **i-Rent stuurt dit pakket als één item** naar OTA's die het ondersteunen
3. **De OTA neemt het op in de boekingsberekening** — geen verborgen verrassingen
4. **Wanneer de boeking terugkomt naar i-Rent**, worden de extra's automatisch weer opgesplitst in aparte regels

## Hoe Het Werkt op Uw Website

Op uw eigen website gedragen extra pakketten zich anders:

- Individuele extra's worden apart weergegeven voor volledige transparantie
- U kunt kiezen om **bepaalde items onzichtbaar op te nemen** in de huurprijs
- Of ze te **tonen als aparte regels** in de prijsopbouw

Deze flexibiliteit stelt u in staat om items zoals administratiekosten, beddengoed of schoonmaak tijdens het verblijf te verwerken zonder vragen van gasten op te roepen.

## Veelvoorkomende Toepassingen

- **Administratiekosten** — Naadloos opnemen in de basisprijs
- **Beddengoed en handdoeken** — Bundelen als verplicht pakket
- **Schoonmaak tijdens verblijf** — Automatisch toevoegen aan langere boekingen
- **Welkomstpakketten** — Meerdere items combineren in één extra

## Aan de Slag

Om het volledige potentieel van extra pakketten voor uw accommodaties te ontdekken, neem contact op met onze helpdesk voor persoonlijke hulp. We helpen u bij het opzetten van een configuratie die het beste werkt voor uw bedrijf.
    `,

    // Article 17: flexible-pricing-ota-partners
    'blog.article.ota-pricing.title': 'Flexibele Prijsaanpassingen voor OTA\'s en Agenten',
    'blog.article.ota-pricing.excerpt': 'Neem de controle over uw omzetstrategie met flexibele prijsaanpassingen — stel aangepaste opslagen in per partner, per accommodatie en per periode direct in i-Rent.',
    'blog.article.ota-pricing.content': `
## Een Nieuwe Tool voor Slimmere Prijsstelling

We hebben een krachtige nieuwe functie aan i-Rent toegevoegd die u volledige controle geeft over hoe prijzen worden aangepast voor uw verhuurpartners, inclusief OTA's en agenten.

U vindt deze tool onder **Beheerder → Externe Prijsinstellingen**.

## Wat Kunt U Doen?

Met deze functie kunt u prijzen aanpassen:

- **Per verhuurpartner** — Pas verschillende opslagen toe voor Booking.com, Airbnb, agenten, etc.
- **Per accommodatie** — Stel specifieke aanpassingen in voor individuele accommodaties of groepen
- **Per periode** — Verhoog prijzen tijdens het hoogseizoen of verlaag ze in het laagseizoen

## Stapsgewijze Instelling

### Stap 1: Definieer Uw Prijsaanpassing

Kies een naam voor de aanpassing, stel het percentage verhoging (of verlaging) in en selecteer het datumbereik.

### Stap 2: Selecteer Accommodaties

Kies op welke accommodaties deze aanpassing van toepassing is — u kunt alle selecteren of specifieke kiezen.

### Stap 3: Selecteer Partners

Kies welke verhuurpartners deze prijsaanpassing moeten ontvangen.

## Onbeperkte Flexibiliteit

U kunt zoveel verschillende prijsaanpassingen maken als u nodig hebt:

- Verschillende opslagen voor verschillende partnergroepen
- Seizoensvariaties in uw portfolio
- Accommodatie-specifieke strategieën voor veelgevraagde accommodaties

**Pro tip:** U kunt ook negatieve percentages instellen om prijzen te verlagen — perfect voor speciale tarieven aan voorkeurpartners of tijdens rustigere perioden.

## Begin Met Het Optimaliseren van Uw Omzet

Deze functie geeft u de controle over uw prijsstrategie over alle kanalen. Stel het eenmalig in en laat i-Rent de rest automatisch afhandelen.
    `,

    // Article 18: marriott-homes-villas-channel-manager
    'blog.article.marriott.title': 'Nieuw Kanaal: Homes & Villas van Marriott International',
    'blog.article.marriott.excerpt': 'Bereik premium reizigers wereldwijd via Marriott\'s gecureerde vakantieverhuurplatform — nu beschikbaar via i-Rent\'s Channel Manager.',
    'blog.article.marriott.content': `
## Introductie van Homes & Villas van Marriott International

[Homes & Villas](https://homes-and-villas.marriott.com/) is het wereldwijde vakantieverhuuranbod van Marriott International, gelanceerd in 2019. Met meer dan 90 jaar hospitalityexpertise heeft Marriott zijn legendarische servicestandaarden naar de vakantieverhuurmarkt gebracht.

## Focus op Premium Accommodaties

In tegenstelling tot massa-boekingsplatforms richt Homes & Villas zich uitsluitend op **premium en luxe verhuuraccommodaties**. Marriott selecteert zorgvuldig professionele vastgoedbeheerders als partners, om ervoor te zorgen dat elke genoteerde accommodatie voldoet aan de hoge standaarden die gasten verwachten van de naam Marriott.

## Waarom Dit Belangrijk Is voor Uw Bedrijf

### Toegang tot Marriott's Wereldwijde Publiek
Met Marriott's enorme klantenbestand van loyale reizigers geeft een vermelding op Homes & Villas uw premium accommodaties blootstelling aan miljoenen potentiële gasten die kwaliteit en betrouwbaarheid waarderen.

### Professionele Standaarden
Deel uitmaken van het Marriott-netwerk signaleert gasten dat uw accommodatie is geverifieerd en voldoet aan internationale hospitalitystandaarden — direct vertrouwen opbouwend.

### Naadloze Integratie
Via i-Rent's Channel Manager kunt u nu uw vermeldingen verbinden met Homes & Villas met volledige synchronisatie van beschikbaarheid, prijzen en boekingen.

## Hoe te Beginnen

1. **Zorg ervoor dat uw accommodaties voldoen aan premium standaarden** — Marriott richt zich op hoogwaardige verhuur
2. **Neem contact op met i-Rent support** om uw interesse in het Homes & Villas kanaal te uiten
3. **Voltooi de partneraanvraag** bij Marriott
4. **Begin met het ontvangen van boekingen** die direct synchroniseren naar uw i-Rent dashboard

## Til Uw Portfolio naar een Hoger Niveau

Als u premium vakantieverhuur beheert, biedt Homes & Villas van Marriott International een uitstekende kans om veeleisende reizigers te bereiken die niets minder dan het beste verwachten. Neem contact met ons op om meer te leren over het verbinden van uw accommodaties met dit prestigieuze platform.
    `,

    // Article 19: bed-sizes-accommodation-description
    'blog.article.bed-sizes.title': 'Voeg Bedmaten Toe aan Uw Accommodatiebeschrijving',
    'blog.article.bed-sizes.excerpt': 'Bedmaten zijn belangrijk voor gasten — nu kunt u ze automatisch opnemen in uw accommodatiebeschrijvingen, vragen verminderen en reizigers helpen met vertrouwen te boeken.',
    'blog.article.bed-sizes.content': `
## Waarom Bedmaten Belangrijk Zijn

Bedmaten zijn vaak een beslissende factor voor gasten bij het kiezen van hun accommodatie. Van koppels die een kingsize bed zoeken tot families die specifieke arrangementen voor kinderen nodig hebben, deze informatie beïnvloedt direct boekingsbeslissingen.

Door bedafmetingen op te nemen in uw accommodatiebeschrijvingen, vermindert u gastvragen, voorkomt u misverstanden en creëert u een transparantere boekingservaring.

## Hoe Bedmaten Toe te Voegen in i-Rent

Volg deze eenvoudige stappen om bedmaten op te nemen in uw accommodatiebeschrijvingen:

1. **Open het accommodatie-informatieblad** in i-Rent.net
2. Navigeer naar de **verdiepingssectie** waar slaapkamers worden beschreven
3. Zoek naar de optie om **bedden toe te voegen of te bewerken**
4. Selecteer de juiste bedtypes en -maten uit de beschikbare opties
5. **Sla het informatieblad op**
6. Ga naar de **accommodatiebeschrijving** en regenereer deze om de bijgewerkte bedinformatie op te nemen

## Belangrijke Opmerking

Bij het regenereren van de accommodatiebeschrijving worden alle **handmatige tekstwijzigingen** die u eerder heeft gemaakt overschreven.

Als u de beschrijvingstekst hebt aangepast, zorg er dan voor dat u **deze wijzigingen kopieert en opslaat** voordat u regenereert — u kunt ze daarna weer toevoegen.

## Voordelen van het Opnemen van Bedmaten

- **Minder gastvragen** — Verstrek de informatie die reizigers vooraf nodig hebben
- **Snellere boekingsbeslissingen** — Help gasten te bevestigen dat de accommodatie aan hun behoeften voldoet
- **Minder klachten** — Stel duidelijke verwachtingen voor aankomst
- **Professionele presentatie** — Toon aandacht voor detail in uw vermeldingen

## Begin Met Het Bijwerken van Uw Vermeldingen

Neem een paar minuten om bedmaten toe te voegen aan uw accommodatie-informatiebladen. Deze kleine verbetering kan een groot verschil maken in gasttevredenheid en boekingsconversies.
    `,

    // Article 20: price-adjustment-calculation-otas
    'blog.article.price-calculation.title': 'Verbeterde Prijsaanpassingsberekening voor OTA\'s en Agenten',
    'blog.article.price-calculation.excerpt': 'We hebben geüpdatet hoe prijsopslagen worden berekend — om ervoor te zorgen dat uw basis huurbedrag nauwkeurig behouden blijft bij het toepassen van procentuele verhogingen voor kanalen.',
    'blog.article.price-calculation.content': `
## Een Slimmere Manier om Prijsopslagen te Berekenen

We hebben een belangrijke wijziging aangebracht in hoe prijsverhogingen worden berekend bij publicatie naar OTA's en agenten. Deze update zorgt ervoor dat uw prijsstelling nauwkeurig en voorspelbaar blijft.

## Het Probleem met de Oude Methode

Voorheen werden prijsopslagen berekend door een percentage toe te voegen aan de basisprijs:

**Voorbeeld:** Basis huurprijs €900 + 10% (€90) = OTA-prijs €990

Het probleem? Wanneer de OTA hun 10% commissie van €990 aftrekt, ontvangt u €891 — niet uw oorspronkelijke €900 basisprijs. Dit creëerde een gat in uw verwachte inkomsten.

## De Nieuwe Berekeningsmethode

De bijgewerkte berekening werkt achteruit vanaf de verkoopprijs, om ervoor te zorgen dat u na aftrek van het OTA-percentage precies uw basis huurbedrag ontvangt.

Met andere woorden: als u een opslag van 10% instelt en de OTA 10% neemt, is uw nettoresultaat nu precies €900 — zoals bedoeld.

## Visuele Updates op de Boekingspagina

Om volledige transparantie te bieden, hebben we de boekingsinterface bijgewerkt:

- Onder het **Prijs tabblad** wordt het opslagbedrag nu in blauw weergegeven
- U kunt altijd uw basis huurbedrag en de toegevoegde verhoging apart zien
- Een nieuwe kolom in het **Algemeen Boekingsoverzicht** toont deze bedragen in één oogopslag
- Een verklarend bericht verschijnt in het groene infoveld op de boekingspagina

## Bescherm Uw Marges

Deze update helpt u gezonde marges te behouden bij het werken met OTA's en agenten. Geen onverwachte inkomstengaten meer — alleen nauwkeurige, voorspelbare prijsstelling over al uw kanalen.
    `,
  },

  it: {
    // Article 16: extra-packages-ota-bookings
    'blog.article.extra-packages.title': 'Nuove Possibilità per Addebitare Servizi Extra',
    'blog.article.extra-packages.excerpt': 'Raggruppa più extra in un unico pacchetto per le prenotazioni OTA — garantendo prezzi accurati mantenendo piena trasparenza sul tuo sito web.',
    'blog.article.extra-packages.content': `
## Strumento Extra Migliorato in i-Rent

Abbiamo migliorato la funzionalità degli extra in i-Rent con nuove potenti funzioni progettate per risolvere una sfida comune del settore.

## Il Problema

Le piattaforme OTA offrono opzioni limitate per calcolare gli extra nelle prenotazioni online. Tipicamente, gli extra che le OTA non possono gestire vengono semplicemente aggiunti come testo nella descrizione dell'alloggio. Il risultato? Gli ospiti spesso trascurano queste informazioni, portando a dispute e discussioni scomode al check-in.

## La Soluzione: Pacchetti Extra

Ora puoi raggruppare più extra in un unico **pacchetto extra**.

Ecco come funziona:

1. **Crea un pacchetto extra** combinando diversi extra individuali
2. **i-Rent invia questo pacchetto come un singolo articolo** alle OTA che lo supportano
3. **L'OTA lo include nel calcolo della prenotazione** — nessuna sorpresa nascosta
4. **Quando la prenotazione ritorna a i-Rent**, gli extra vengono automaticamente divisi in voci separate

## Come Funziona sul Tuo Sito Web

Sul tuo sito web, i pacchetti extra si comportano diversamente:

- Gli extra individuali vengono visualizzati separatamente per piena trasparenza
- Puoi scegliere di **includere certi articoli invisibilmente** nel prezzo di affitto
- Oppure **mostrarli come voci separate** nel dettaglio del prezzo

Questa flessibilità ti permette di gestire articoli come spese amministrative, biancheria da letto o pulizie durante il soggiorno senza sollevare domande dagli ospiti.

## Casi d'Uso Comuni

- **Costi amministrativi** — Includere nel prezzo base senza problemi
- **Biancheria e asciugamani** — Raggruppare come pacchetto obbligatorio
- **Pulizie durante il soggiorno** — Aggiungere automaticamente alle prenotazioni lunghe
- **Pacchetti di benvenuto** — Combinare più articoli in un extra

## Inizia

Per scoprire il pieno potenziale dei pacchetti extra per le tue proprietà, contatta il nostro helpdesk per assistenza personalizzata. Ti aiuteremo a configurare una configurazione che funzioni meglio per la tua attività.
    `,

    // Article 17: flexible-pricing-ota-partners
    'blog.article.ota-pricing.title': 'Adeguamenti di Prezzo Flessibili per OTA e Agenti',
    'blog.article.ota-pricing.excerpt': 'Prendi il controllo della tua strategia di ricavi con adeguamenti di prezzo flessibili — imposta maggiorazioni personalizzate per partner, per proprietà e per periodo direttamente in i-Rent.',
    'blog.article.ota-pricing.content': `
## Un Nuovo Strumento per Prezzi Più Intelligenti

Abbiamo aggiunto una nuova potente funzionalità a i-Rent che ti dà il controllo completo su come i prezzi vengono adeguati per i tuoi partner di affitto, incluse OTA e agenti.

Troverai questo strumento sotto **Amministratore → Impostazioni Prezzi Esterni**.

## Cosa Puoi Fare?

Con questa funzionalità, puoi adeguare i prezzi:

- **Per partner di affitto** — Applica diverse maggiorazioni per Booking.com, Airbnb, agenti, ecc.
- **Per alloggio** — Imposta adeguamenti specifici per proprietà individuali o gruppi
- **Per periodo** — Aumenta i prezzi durante l'alta stagione o riducili in bassa stagione

## Configurazione Passo Passo

### Passo 1: Definisci il Tuo Adeguamento di Prezzo

Scegli un nome per l'adeguamento, imposta la percentuale di aumento (o diminuzione) e seleziona l'intervallo di date.

### Passo 2: Seleziona gli Alloggi

Scegli a quali proprietà si applica questo adeguamento — puoi selezionarle tutte o sceglierne di specifiche.

### Passo 3: Seleziona i Partner

Scegli quali partner di affitto dovrebbero ricevere questo adeguamento di prezzo.

## Flessibilità Illimitata

Puoi creare quanti adeguamenti di prezzo diversi ti servono:

- Diverse maggiorazioni per diversi gruppi di partner
- Variazioni stagionali nel tuo portfolio
- Strategie specifiche per proprietà ad alta domanda

**Consiglio pro:** Puoi anche impostare percentuali negative per ridurre i prezzi — perfetto per offrire tariffe speciali a partner privilegiati o durante periodi più tranquilli.

## Inizia a Ottimizzare i Tuoi Ricavi

Questa funzionalità ti mette al comando della tua strategia di prezzi su tutti i canali. Configurala una volta e lascia che i-Rent gestisca il resto automaticamente.
    `,

    // Article 18: marriott-homes-villas-channel-manager
    'blog.article.marriott.title': 'Nuovo Canale: Homes & Villas di Marriott International',
    'blog.article.marriott.excerpt': 'Raggiungi viaggiatori premium in tutto il mondo attraverso la piattaforma curata di affitti vacanza di Marriott — ora disponibile tramite il Channel Manager di i-Rent.',
    'blog.article.marriott.content': `
## Presentazione di Homes & Villas di Marriott International

[Homes & Villas](https://homes-and-villas.marriott.com/) è l'offerta globale di affitto vacanze di Marriott International, lanciata nel 2019. Con oltre 90 anni di esperienza nell'ospitalità, Marriott ha portato i suoi leggendari standard di servizio nel mercato degli affitti vacanza.

## Focus su Proprietà Premium

A differenza delle piattaforme di prenotazione di massa, Homes & Villas si concentra esclusivamente su **proprietà di affitto premium e di lusso**. Marriott seleziona attentamente gestori di proprietà professionisti come partner, assicurando che ogni proprietà elencata soddisfi gli alti standard che gli ospiti si aspettano dal nome Marriott.

## Perché Questo È Importante per la Tua Attività

### Accesso al Pubblico Globale di Marriott
Con l'enorme base di clienti di Marriott di viaggiatori fedeli, elencare su Homes & Villas dà alle tue proprietà premium esposizione a milioni di potenziali ospiti che apprezzano qualità e affidabilità.

### Standard Professionali
Essere parte della rete Marriott segnala agli ospiti che la tua proprietà è stata verificata e soddisfa gli standard internazionali di ospitalità — costruendo fiducia istantanea.

### Integrazione Senza Problemi
Attraverso il Channel Manager di i-Rent, ora puoi connettere i tuoi annunci a Homes & Villas con sincronizzazione completa di disponibilità, prezzi e prenotazioni.

## Come Iniziare

1. **Assicurati che le tue proprietà soddisfino standard premium** — Marriott si concentra su affitti di alta qualità
2. **Contatta il supporto i-Rent** per esprimere il tuo interesse nel canale Homes & Villas
3. **Completa la domanda di partnership** con Marriott
4. **Inizia a ricevere prenotazioni** sincronizzate direttamente con la tua dashboard i-Rent

## Eleva il Tuo Portfolio

Se gestisci affitti vacanza premium, Homes & Villas di Marriott International offre un'eccellente opportunità per raggiungere viaggiatori esigenti che si aspettano niente di meno che il meglio. Contattaci per saperne di più sulla connessione delle tue proprietà a questa prestigiosa piattaforma.
    `,

    // Article 19: bed-sizes-accommodation-description
    'blog.article.bed-sizes.title': 'Aggiungi le Dimensioni del Letto alla Descrizione del Tuo Alloggio',
    'blog.article.bed-sizes.excerpt': 'Le dimensioni del letto sono importanti per gli ospiti — ora puoi includerle automaticamente nelle descrizioni delle tue proprietà, riducendo le domande e aiutando i viaggiatori a prenotare con fiducia.',
    'blog.article.bed-sizes.content': `
## Perché le Dimensioni del Letto Sono Importanti

Le dimensioni del letto sono spesso un fattore decisivo per gli ospiti nella scelta del loro alloggio. Dalle coppie che cercano un letto king-size alle famiglie che necessitano di sistemazioni specifiche per i bambini, queste informazioni influenzano direttamente le decisioni di prenotazione.

Includendo le dimensioni dei letti nelle descrizioni delle tue proprietà, riduci le richieste degli ospiti, eviti malintesi e crei un'esperienza di prenotazione più trasparente.

## Come Aggiungere le Dimensioni del Letto in i-Rent

Segui questi semplici passaggi per includere le dimensioni del letto nelle descrizioni del tuo alloggio:

1. **Apri la scheda informativa dell'alloggio** in i-Rent.net
2. Naviga alla **sezione piano** dove sono descritte le camere da letto
3. Cerca l'opzione per **aggiungere o modificare i letti**
4. Seleziona i tipi e le dimensioni di letto appropriati dalle opzioni disponibili
5. **Salva la scheda informativa**
6. Vai alla **descrizione dell'alloggio** e rigenerala per includere le informazioni sui letti aggiornate

## Nota Importante

Quando rigeneri la descrizione dell'alloggio, qualsiasi **modifica testuale manuale** che hai fatto in precedenza verrà sovrascritta.

Se hai personalizzato il testo della descrizione, assicurati di **copiare e salvare quelle modifiche** prima di rigenerare — puoi aggiungerle di nuovo dopo.

## Vantaggi dell'Inclusione delle Dimensioni del Letto

- **Meno domande degli ospiti** — Fornisci le informazioni di cui i viaggiatori hanno bisogno in anticipo
- **Decisioni di prenotazione più rapide** — Aiuta gli ospiti a confermare che la proprietà soddisfa le loro esigenze
- **Meno reclami** — Stabilisci aspettative chiare prima dell'arrivo
- **Presentazione professionale** — Mostra attenzione ai dettagli nei tuoi annunci

## Inizia ad Aggiornare i Tuoi Annunci

Prenditi qualche minuto per aggiungere le dimensioni del letto alle schede informative delle tue proprietà. Questo piccolo miglioramento può fare una grande differenza nella soddisfazione degli ospiti e nelle conversioni delle prenotazioni.
    `,

    // Article 20: price-adjustment-calculation-otas
    'blog.article.price-calculation.title': 'Calcolo Migliorato dell\'Adeguamento dei Prezzi per OTA e Agenti',
    'blog.article.price-calculation.excerpt': 'Abbiamo aggiornato come vengono calcolate le maggiorazioni di prezzo — assicurando che il tuo importo base di affitto sia preservato accuratamente quando applichi aumenti percentuali per i canali.',
    'blog.article.price-calculation.content': `
## Un Modo Più Intelligente per Calcolare le Maggiorazioni di Prezzo

Abbiamo apportato un cambiamento importante al modo in cui gli aumenti di prezzo vengono calcolati quando si pubblica su OTA e agenti. Questo aggiornamento assicura che i tuoi prezzi rimangano accurati e prevedibili.

## Il Problema con il Vecchio Metodo

In precedenza, le maggiorazioni di prezzo venivano calcolate aggiungendo una percentuale al prezzo base:

**Esempio:** Prezzo base di affitto €900 + 10% (€90) = Prezzo OTA €990

Il problema? Quando l'OTA deduce la sua commissione del 10% da €990, ricevi €891 — non i tuoi €900 originali di prezzo base. Questo creava un gap nei tuoi ricavi attesi.

## Il Nuovo Metodo di Calcolo

Il calcolo aggiornato lavora a ritroso dal prezzo di vendita, assicurando che dopo che l'OTA deduce la sua percentuale, tu riceva esattamente il tuo importo base di affitto.

In altre parole: se imposti una maggiorazione del 10% e l'OTA prende il 10%, il tuo risultato netto è ora precisamente €900 — come previsto.

## Aggiornamenti Visivi nella Pagina di Prenotazione

Per fornire piena trasparenza, abbiamo aggiornato l'interfaccia di prenotazione:

- Sotto la **scheda Prezzo**, l'importo della maggiorazione è ora visualizzato in blu
- Puoi sempre vedere il tuo importo base di affitto e l'aumento aggiunto separatamente
- Una nuova colonna nella **Panoramica Generale delle Prenotazioni** mostra questi importi a colpo d'occhio
- Un messaggio esplicativo appare nel campo info verde sulla pagina di prenotazione

## Proteggi i Tuoi Margini

Questo aggiornamento ti aiuta a mantenere margini sani quando lavori con OTA e agenti. Niente più gap di ricavi inaspettati — solo prezzi accurati e prevedibili su tutti i tuoi canali.
    `,
  },

  pt: {
    // Article 16: extra-packages-ota-bookings
    'blog.article.extra-packages.title': 'Novas Possibilidades para Cobrar Serviços Extra',
    'blog.article.extra-packages.excerpt': 'Agrupe múltiplos extras num único pacote para reservas OTA — garantindo preços precisos enquanto mantém total transparência no seu próprio website.',
    'blog.article.extra-packages.content': `
## Ferramenta de Extras Melhorada no i-Rent

Melhorámos a funcionalidade de extras no i-Rent com novas funcionalidades poderosas desenhadas para resolver um desafio comum da indústria.

## O Problema

As plataformas OTA oferecem opções limitadas para calcular extras em reservas online. Tipicamente, extras que as OTAs não conseguem processar são simplesmente adicionados como texto na descrição do alojamento. O resultado? Os hóspedes frequentemente ignoram esta informação, levando a disputas e discussões desconfortáveis no check-in.

## A Solução: Pacotes de Extras

Agora pode agrupar múltiplos extras num único **pacote de extras**.

Veja como funciona:

1. **Crie um pacote de extras** combinando vários extras individuais
2. **O i-Rent envia este pacote como um único item** para as OTAs que o suportam
3. **A OTA inclui-o no cálculo da reserva** — sem surpresas escondidas
4. **Quando a reserva retorna ao i-Rent**, os extras são automaticamente divididos em linhas separadas

## Como Funciona no Seu Website

No seu próprio website, os pacotes de extras comportam-se de forma diferente:

- Extras individuais são exibidos separadamente para total transparência
- Pode escolher **incluir certos itens invisivelmente** no preço do aluguer
- Ou **mostrá-los como linhas separadas** na discriminação de preços

Esta flexibilidade permite-lhe lidar com itens como custos de administração, roupa de cama ou limpeza durante a estadia sem levantar questões dos hóspedes.

## Casos de Uso Comuns

- **Custos de administração** — Incluir no preço base sem problemas
- **Roupa de cama e toalhas** — Agrupar como pacote obrigatório
- **Limpeza durante a estadia** — Adicionar automaticamente a reservas longas
- **Pacotes de boas-vindas** — Combinar múltiplos itens num extra

## Comece

Para descobrir o potencial completo dos pacotes de extras para as suas propriedades, contacte o nosso helpdesk para assistência personalizada. Ajudá-lo-emos a configurar uma configuração que funcione melhor para o seu negócio.
    `,

    // Article 17: flexible-pricing-ota-partners
    'blog.article.ota-pricing.title': 'Ajustes de Preço Flexíveis para OTAs e Agentes',
    'blog.article.ota-pricing.excerpt': 'Tome o controlo da sua estratégia de receitas com ajustes de preço flexíveis — defina margens personalizadas por parceiro, por propriedade e por período diretamente no i-Rent.',
    'blog.article.ota-pricing.content': `
## Uma Nova Ferramenta para Preços Mais Inteligentes

Adicionámos uma nova funcionalidade poderosa ao i-Rent que lhe dá controlo completo sobre como os preços são ajustados para os seus parceiros de aluguer, incluindo OTAs e agentes.

Encontrará esta ferramenta em **Administrador → Configurações de Preços Externos**.

## O Que Pode Fazer?

Com esta funcionalidade, pode ajustar preços:

- **Por parceiro de aluguer** — Aplique diferentes margens para Booking.com, Airbnb, agentes, etc.
- **Por alojamento** — Defina ajustes específicos para propriedades individuais ou grupos
- **Por período** — Aumente preços durante a época alta ou reduza-os na época baixa

## Configuração Passo a Passo

### Passo 1: Defina o Seu Ajuste de Preço

Escolha um nome para o ajuste, defina a percentagem de aumento (ou diminuição) e selecione o intervalo de datas.

### Passo 2: Selecione Alojamentos

Escolha a quais propriedades este ajuste se aplica — pode selecionar todas ou escolher específicas.

### Passo 3: Selecione Parceiros

Escolha quais parceiros de aluguer devem receber este ajuste de preço.

## Flexibilidade Ilimitada

Pode criar tantos ajustes de preço diferentes quantos precisar:

- Diferentes margens para diferentes grupos de parceiros
- Variações sazonais no seu portfolio
- Estratégias específicas por propriedade para alojamentos de alta procura

**Dica pro:** Também pode definir percentagens negativas para reduzir preços — perfeito para oferecer tarifas especiais a parceiros preferenciais ou durante períodos mais calmos.

## Comece a Otimizar as Suas Receitas

Esta funcionalidade coloca-o no controlo da sua estratégia de preços em todos os canais. Configure-a uma vez e deixe o i-Rent tratar do resto automaticamente.
    `,

    // Article 18: marriott-homes-villas-channel-manager
    'blog.article.marriott.title': 'Novo Canal: Homes & Villas da Marriott International',
    'blog.article.marriott.excerpt': 'Alcance viajantes premium em todo o mundo através da plataforma curada de aluguer de férias da Marriott — agora disponível via Channel Manager do i-Rent.',
    'blog.article.marriott.content': `
## Apresentamos Homes & Villas da Marriott International

[Homes & Villas](https://homes-and-villas.marriott.com/) é a oferta global de aluguer de férias da Marriott International, lançada em 2019. Com mais de 90 anos de experiência em hospitalidade, a Marriott trouxe os seus lendários padrões de serviço para o mercado de aluguer de férias.

## Foco em Propriedades Premium

Ao contrário das plataformas de reserva de massa, Homes & Villas foca-se exclusivamente em **propriedades de aluguer premium e de luxo**. A Marriott seleciona cuidadosamente gestores de propriedades profissionais como parceiros, garantindo que cada propriedade listada cumpre os altos padrões que os hóspedes esperam do nome Marriott.

## Porque Isto É Importante para o Seu Negócio

### Acesso ao Público Global da Marriott
Com a vasta base de clientes da Marriott de viajantes leais, listar no Homes & Villas dá às suas propriedades premium exposição a milhões de potenciais hóspedes que valorizam qualidade e fiabilidade.

### Padrões Profissionais
Fazer parte da rede Marriott sinaliza aos hóspedes que a sua propriedade foi verificada e cumpre padrões internacionais de hospitalidade — construindo confiança instantânea.

### Integração Perfeita
Através do Channel Manager do i-Rent, pode agora conectar os seus anúncios ao Homes & Villas com sincronização completa de disponibilidade, preços e reservas.

## Como Começar

1. **Certifique-se de que as suas propriedades cumprem padrões premium** — A Marriott foca-se em alugueres de alta qualidade
2. **Contacte o suporte do i-Rent** para expressar o seu interesse no canal Homes & Villas
3. **Complete a candidatura de parceiro** com a Marriott
4. **Comece a receber reservas** sincronizadas diretamente com o seu dashboard do i-Rent

## Eleve o Seu Portfolio

Se gere alugueres de férias premium, Homes & Villas da Marriott International oferece uma excelente oportunidade para alcançar viajantes exigentes que esperam nada menos que o melhor. Contacte-nos para saber mais sobre conectar as suas propriedades a esta prestigiada plataforma.
    `,

    // Article 19: bed-sizes-accommodation-description
    'blog.article.bed-sizes.title': 'Adicione Tamanhos de Cama à Descrição do Seu Alojamento',
    'blog.article.bed-sizes.excerpt': 'Os tamanhos de cama são importantes para os hóspedes — agora pode incluí-los automaticamente nas descrições das suas propriedades, reduzindo perguntas e ajudando viajantes a reservar com confiança.',
    'blog.article.bed-sizes.content': `
## Porque os Tamanhos de Cama São Importantes

Os tamanhos de cama são frequentemente um fator decisivo para os hóspedes ao escolher o seu alojamento. Desde casais à procura de uma cama king-size até famílias que precisam de arranjos específicos para crianças, esta informação influencia diretamente as decisões de reserva.

Ao incluir as dimensões das camas nas descrições das suas propriedades, reduz as perguntas dos hóspedes, evita mal-entendidos e cria uma experiência de reserva mais transparente.

## Como Adicionar Tamanhos de Cama no i-Rent

Siga estes passos simples para incluir tamanhos de cama nas descrições do seu alojamento:

1. **Abra a ficha de informação do alojamento** em i-Rent.net
2. Navegue até à **secção do piso** onde os quartos são descritos
3. Procure a opção de **adicionar ou editar camas**
4. Selecione os tipos e tamanhos de cama apropriados das opções disponíveis
5. **Guarde a ficha de informação**
6. Vá à **descrição do alojamento** e regenere-a para incluir a informação atualizada das camas

## Nota Importante

Ao regenerar a descrição do alojamento, quaisquer **alterações de texto manuais** que tenha feito anteriormente serão substituídas.

Se personalizou o texto da descrição, certifique-se de **copiar e guardar essas alterações** antes de regenerar — pode adicioná-las novamente depois.

## Benefícios de Incluir Tamanhos de Cama

- **Menos perguntas dos hóspedes** — Forneça a informação que os viajantes precisam antecipadamente
- **Decisões de reserva mais rápidas** — Ajude os hóspedes a confirmar que a propriedade satisfaz as suas necessidades
- **Menos reclamações** — Defina expectativas claras antes da chegada
- **Apresentação profissional** — Mostre atenção ao detalhe nos seus anúncios

## Comece a Atualizar os Seus Anúncios

Dedique alguns minutos para adicionar tamanhos de cama às fichas de informação das suas propriedades. Esta pequena melhoria pode fazer uma grande diferença na satisfação dos hóspedes e nas conversões de reservas.
    `,

    // Article 20: price-adjustment-calculation-otas
    'blog.article.price-calculation.title': 'Cálculo Melhorado de Ajuste de Preço para OTAs e Agentes',
    'blog.article.price-calculation.excerpt': 'Atualizámos como as margens de preço são calculadas — garantindo que o seu valor base de aluguer é preservado com precisão ao aplicar aumentos percentuais para canais.',
    'blog.article.price-calculation.content': `
## Uma Forma Mais Inteligente de Calcular Margens de Preço

Fizemos uma alteração importante na forma como os aumentos de preço são calculados ao publicar para OTAs e agentes. Esta atualização garante que os seus preços permanecem precisos e previsíveis.

## O Problema com o Método Antigo

Anteriormente, as margens de preço eram calculadas adicionando uma percentagem ao preço base:

**Exemplo:** Preço base de aluguer €900 + 10% (€90) = Preço OTA €990

O problema? Quando a OTA deduz a sua comissão de 10% de €990, recebe €891 — não os seus €900 originais de preço base. Isto criava uma lacuna nas suas receitas esperadas.

## O Novo Método de Cálculo

O cálculo atualizado funciona de trás para a frente a partir do preço de venda, garantindo que depois da OTA deduzir a sua percentagem, recebe exatamente o seu valor base de aluguer.

Por outras palavras: se definir uma margem de 10% e a OTA tirar 10%, o seu resultado líquido é agora precisamente €900 — como pretendido.

## Atualizações Visuais na Página de Reserva

Para fornecer total transparência, atualizámos a interface de reservas:

- Sob o **separador Preço**, o valor da margem é agora exibido em azul
- Pode sempre ver o seu valor base de aluguer e o aumento adicionado separadamente
- Uma nova coluna na **Visão Geral de Reservas** mostra estes valores de relance
- Uma mensagem explicativa aparece no campo de informação verde na página de reserva

## Proteja as Suas Margens

Esta atualização ajuda-o a manter margens saudáveis ao trabalhar com OTAs e agentes. Sem mais lacunas de receita inesperadas — apenas preços precisos e previsíveis em todos os seus canais.
    `,
  },

  dk: {
    // Article 16: extra-packages-ota-bookings
    'blog.article.extra-packages.title': 'Nye Muligheder for at Opkræve Ekstraydelser',
    'blog.article.extra-packages.excerpt': 'Bundt flere ekstras i én pakke til OTA-bookinger — sikrer præcis prissætning mens du opretholder fuld gennemsigtighed på din egen hjemmeside.',
    'blog.article.extra-packages.content': `
## Forbedret Ekstra-værktøj i i-Rent

Vi har forbedret ekstrafunktionaliteten i i-Rent med kraftfulde nye funktioner designet til at løse en almindelig brancheudfordring.

## Problemet

OTA-platforme tilbyder begrænsede muligheder for at beregne ekstras i online-bookinger. Typisk tilføjes ekstras, som OTA'er ikke kan håndtere, blot som tekst i indkvarteringsbeskrivelsen. Resultatet? Gæster overser ofte denne information, hvilket fører til tvister og ubehagelige diskussioner ved check-in.

## Løsningen: Ekstrapakker

Du kan nu bundte flere ekstras i én enkelt **ekstrapakke**.

Sådan fungerer det:

1. **Opret en ekstrapakke** der kombinerer flere individuelle ekstras
2. **i-Rent sender denne pakke som ét element** til OTA'er der understøtter det
3. **OTA'en inkluderer det i bookingberegningen** — ingen skjulte overraskelser
4. **Når bookingen returnerer til i-Rent**, opdeles ekstras automatisk i separate linjer

## Hvordan Det Fungerer på Din Hjemmeside

På din egen hjemmeside opfører ekstrapakker sig anderledes:

- Individuelle ekstras vises separat for fuld gennemsigtighed
- Du kan vælge at **inkludere visse elementer usynligt** i lejeprisen
- Eller **vise dem som separate linjer** i prisopdelingen

Denne fleksibilitet giver dig mulighed for at håndtere elementer som administrationsgebyrer, sengetøj eller rengøring under opholdet uden at rejse spørgsmål fra gæster.

## Almindelige Anvendelsestilfælde

- **Administrationsomkostninger** — Inkluder i basisprisen problemfrit
- **Sengetøj og håndklæder** — Bundt som obligatorisk pakke
- **Rengøring under ophold** — Tilføj automatisk til længere bookinger
- **Velkomstpakker** — Kombiner flere elementer til én ekstra

## Kom i Gang

For at opdage det fulde potentiale af ekstrapakker til dine ejendomme, kontakt vores helpdesk for personlig assistance. Vi hjælper dig med at opsætte en konfiguration der fungerer bedst for din virksomhed.
    `,

    // Article 17: flexible-pricing-ota-partners
    'blog.article.ota-pricing.title': 'Fleksible Prisjusteringer for OTA\'er og Agenter',
    'blog.article.ota-pricing.excerpt': 'Tag kontrol over din indtægtsstrategi med fleksible prisjusteringer — sæt tilpassede tillæg per partner, per ejendom og per periode direkte i i-Rent.',
    'blog.article.ota-pricing.content': `
## Et Nyt Værktøj til Smartere Prissætning

Vi har tilføjet en kraftfuld ny funktion til i-Rent der giver dig fuld kontrol over, hvordan priser justeres for dine udlejningspartnere, inklusive OTA'er og agenter.

Du finder dette værktøj under **Administrator → Eksterne Prisindstillinger**.

## Hvad Kan Du Gøre?

Med denne funktion kan du justere priser:

- **Per udlejningspartner** — Anvend forskellige tillæg for Booking.com, Airbnb, agenter osv.
- **Per indkvartering** — Sæt specifikke justeringer for individuelle ejendomme eller grupper
- **Per tidsperiode** — Øg priser i højsæsonen eller reducer dem i lavsæsonen

## Trin-for-Trin Opsætning

### Trin 1: Definer Din Prisjustering

Vælg et navn til justeringen, sæt procentdelen for stigning (eller fald), og vælg datointervallet.

### Trin 2: Vælg Indkvarteringer

Vælg hvilke ejendomme denne justering gælder for — du kan vælge alle eller vælge specifikke.

### Trin 3: Vælg Partnere

Vælg hvilke udlejningspartnere der skal modtage denne prisjustering.

## Ubegrænset Fleksibilitet

Du kan oprette så mange forskellige prisjusteringer som du har brug for:

- Forskellige tillæg for forskellige partnergrupper
- Sæsonvariationer på tværs af din portefølje
- Ejendomsspecifikke strategier for eftertragtede indkvarteringer

**Pro tip:** Du kan også sætte negative procenter for at reducere priser — perfekt til at tilbyde særlige priser til foretrukne partnere eller i roligere perioder.

## Begynd at Optimere Din Indtægt

Denne funktion giver dig kontrol over din prisstrategi på tværs af alle kanaler. Sæt det op én gang og lad i-Rent håndtere resten automatisk.
    `,

    // Article 18: marriott-homes-villas-channel-manager
    'blog.article.marriott.title': 'Ny Kanal: Homes & Villas af Marriott International',
    'blog.article.marriott.excerpt': 'Nå premium-rejsende verden over gennem Marriotts kuraterede ferieudlejningsplatform — nu tilgængelig via i-Rents Channel Manager.',
    'blog.article.marriott.content': `
## Introduktion af Homes & Villas af Marriott International

[Homes & Villas](https://homes-and-villas.marriott.com/) er Marriott Internationals globale ferieudlejningstilbud, lanceret i 2019. Med over 90 års gæstfrihedsekspertise har Marriott bragt sine legendariske servicestandarder til ferieudlejningsmarkedet.

## Fokus på Premium-Ejendomme

I modsætning til massemarkeds-bookingplatforme fokuserer Homes & Villas udelukkende på **premium- og luksusudlejningsejendomme**. Marriott udvælger omhyggeligt professionelle ejendomsforvaltere som partnere, hvilket sikrer at hver listet ejendom opfylder de høje standarder gæster forventer af Marriott-navnet.

## Hvorfor Dette Er Vigtigt for Din Virksomhed

### Adgang til Marriotts Globale Publikum
Med Marriotts enorme kundebase af loyale rejsende giver en notering på Homes & Villas dine premium-ejendomme eksponering til millioner af potentielle gæster der værdsætter kvalitet og pålidelighed.

### Professionelle Standarder
At være en del af Marriott-netværket signalerer til gæster at din ejendom er blevet verificeret og opfylder internationale gæstfrihedsstandarder — bygger øjeblikkelig tillid.

### Problemfri Integration
Gennem i-Rents Channel Manager kan du nu forbinde dine opslag til Homes & Villas med fuld synkronisering af tilgængelighed, priser og bookinger.

## Sådan Kommer Du i Gang

1. **Sikr dig at dine ejendomme opfylder premium-standarder** — Marriott fokuserer på højkvalitets udlejning
2. **Kontakt i-Rent support** for at udtrykke din interesse i Homes & Villas-kanalen
3. **Fuldfør partneransøgningen** hos Marriott
4. **Begynd at modtage bookinger** synkroniseret direkte til dit i-Rent dashboard

## Løft Din Portefølje

Hvis du administrerer premium-ferieudlejninger, tilbyder Homes & Villas af Marriott International en fremragende mulighed for at nå krævende rejsende der forventer intet mindre end det bedste. Kontakt os for at lære mere om at forbinde dine ejendomme til denne prestigefyldte platform.
    `,

    // Article 19: bed-sizes-accommodation-description
    'blog.article.bed-sizes.title': 'Tilføj Sengestørrelser til Din Indkvarteringsbeskrivelse',
    'blog.article.bed-sizes.excerpt': 'Sengestørrelser betyder noget for gæster — nu kan du inkludere dem automatisk i dine ejendomsbeskrivelser, reducere spørgsmål og hjælpe rejsende med at booke med tillid.',
    'blog.article.bed-sizes.content': `
## Hvorfor Sengestørrelser Er Vigtige

Sengestørrelser er ofte en afgørende faktor for gæster ved valg af indkvartering. Fra par der søger en kingsize-seng til familier der har brug for specifikke arrangementer til børn, påvirker denne information direkte bookingbeslutninger.

Ved at inkludere sengemål i dine ejendomsbeskrivelser reducerer du gæsteforespørgsler, undgår misforståelser og skaber en mere gennemsigtig bookingoplevelse.

## Sådan Tilføjer Du Sengestørrelser i i-Rent

Følg disse enkle trin for at inkludere sengestørrelser i dine indkvarteringsbeskrivelser:

1. **Åbn indkvarteringens infoblad** i i-Rent.net
2. Naviger til **etagedelen** hvor soveværelser beskrives
3. Led efter muligheden for at **tilføje eller redigere senge**
4. Vælg de relevante sengetyper og -størrelser fra de tilgængelige muligheder
5. **Gem infobladet**
6. Gå til **indkvarteringsbeskrivelsen** og regenerer den for at inkludere de opdaterede sengeoplysninger

## Vigtig Bemærkning

Når du regenererer indkvarteringsbeskrivelsen, vil eventuelle **manuelle tekstændringer** du tidligere har lavet blive overskrevet.

Hvis du har tilpasset beskrivelsesteksten, sørg for at **kopiere og gemme disse ændringer** før regenerering — du kan tilføje dem igen bagefter.

## Fordele ved at Inkludere Sengestørrelser

- **Færre gæstespørgsmål** — Giv den information rejsende har brug for på forhånd
- **Hurtigere bookingbeslutninger** — Hjælp gæster med at bekræfte at ejendommen opfylder deres behov
- **Færre klager** — Sæt klare forventninger før ankomst
- **Professionel præsentation** — Vis opmærksomhed på detaljer i dine opslag

## Begynd at Opdatere Dine Opslag

Tag et par minutter til at tilføje sengestørrelser til dine ejendomsinfoblade. Denne lille forbedring kan gøre en stor forskel i gæstetilfredshed og bookingkonverteringer.
    `,

    // Article 20: price-adjustment-calculation-otas
    'blog.article.price-calculation.title': 'Forbedret Prisjusteringsberegning for OTA\'er og Agenter',
    'blog.article.price-calculation.excerpt': 'Vi har opdateret hvordan pristillæg beregnes — sikrer at dit grundlejebeløb bevares nøjagtigt ved anvendelse af procentuelle stigninger for kanaler.',
    'blog.article.price-calculation.content': `
## En Smartere Måde at Beregne Pristillæg

Vi har foretaget en vigtig ændring i hvordan prisstigninger beregnes ved publicering til OTA'er og agenter. Denne opdatering sikrer at din prissætning forbliver nøjagtig og forudsigelig.

## Problemet med den Gamle Metode

Tidligere blev pristillæg beregnet ved at tilføje en procent til basisprisen:

**Eksempel:** Basis lejepris €900 + 10% (€90) = OTA-pris €990

Problemet? Når OTA'en trækker deres 10% kommission fra €990, modtager du €891 — ikke dine oprindelige €900 basispris. Dette skabte et hul i dine forventede indtægter.

## Den Nye Beregningsmetode

Den opdaterede beregning arbejder baglæns fra salgsprisen, hvilket sikrer at efter OTA'en trækker deres procent, modtager du præcis dit grundlejebeløb.

Med andre ord: hvis du sætter et tillæg på 10% og OTA'en tager 10%, er dit nettoresultat nu præcis €900 — som tiltænkt.

## Visuelle Opdateringer på Bookingsiden

For at give fuld gennemsigtighed har vi opdateret bookinggrænsefladen:

- Under **Pris-fanen** vises tillægsbeløbet nu i blåt
- Du kan altid se dit grundlejebeløb og den tilføjede stigning separat
- En ny kolonne i **General Booking Overview** viser disse beløb med et blik
- En forklarende besked vises i det grønne infofelt på bookingsiden

## Beskyt Dine Marginer

Denne opdatering hjælper dig med at opretholde sunde marginer når du arbejder med OTA'er og agenter. Ingen flere uventede indtægtsgab — kun nøjagtig, forudsigelig prissætning på tværs af alle dine kanaler.
    `,
  },
};

// Export slug mappings for batch 4
export const slugToTranslationKeyBatch4: Record<string, string> = {
  'extra-packages-ota-bookings': 'blog.article.extra-packages',
  'flexible-pricing-ota-partners': 'blog.article.ota-pricing',
  'marriott-homes-villas-channel-manager': 'blog.article.marriott',
  'bed-sizes-accommodation-description': 'blog.article.bed-sizes',
  'price-adjustment-calculation-otas': 'blog.article.price-calculation',
};

export const translatedArticleSlugsBatch4 = [
  'extra-packages-ota-bookings',
  'flexible-pricing-ota-partners',
  'marriott-homes-villas-channel-manager',
  'bed-sizes-accommodation-description',
  'price-adjustment-calculation-otas',
];
